import { Box, Dialog, SwipeableDrawer, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: "90%",
    backgroundColor: theme.palette.semantic["surface/card"],
    boxShadow: "none",
    borderRadius: "16px 16px 0 0",
    paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
  },
  pcPaper: { borderRadius: 16, width: 480, maxWidth: "calc(100% - 64px)", padding: 32, gap: 24 },
  backdrop: { backgroundColor: theme.palette.semantic["surface/overlay"] },
  handle: { display: "flex", justifyContent: "center", padding: "8px 0 12px", flexShrink: 0 },
  bar: { width: 36, height: 4, borderRadius: 2, backgroundColor: theme.palette.semantic["border/strong"] },
  head: { padding: "8px 24px", flexShrink: 0 },
  pcHead: { padding: "8px 0" },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 20 } },
  titleBreakWord: { overflowWrap: "anywhere" },
  description: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: 4,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  body: { minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" },
  footer: { padding: "0 24px", flexShrink: 0 },
  pcFooter: { padding: 0 },
}));

interface BottomSheetProps {
  open: boolean;
  title: string;
  titleBreakWord?: boolean;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onOpen: () => void;
  onClose: () => void;
  contentPadding?: 0 | 24;
  contentClassName?: string;
  maxHeightPercent?: number;
  zIndex?: number;
}

/** 開閉と操作は呼び出し元が管理する。SPはシート、PCは中央ダイアログ。 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  title,
  titleBreakWord = false,
  description,
  children,
  footer,
  onOpen,
  onClose,
  contentPadding = 24,
  contentClassName,
  maxHeightPercent = 90,
  zIndex,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const content = (
    <>
      {isMobile && (
        <Box className={classes.handle} data-sheet-handle>
          <span className={classes.bar} />
        </Box>
      )}
      <Box className={`${classes.head} ${!isMobile ? classes.pcHead : ""}`}>
        <Typography component="h2" className={`${classes.title} ${titleBreakWord ? classes.titleBreakWord : ""}`}>
          {title}
        </Typography>
        {description && (
          <Typography component="div" className={classes.description}>
            {description}
          </Typography>
        )}
      </Box>
      <Box
        className={`${classes.body} ${contentClassName || ""}`}
        style={{ padding: isMobile ? `0 ${contentPadding}px` : 0 }}
        data-sheet-body
      >
        {children}
      </Box>
      {footer && (
        <Box className={`${classes.footer} ${!isMobile ? classes.pcFooter : ""}`} data-sheet-footer>
          {footer}
        </Box>
      )}
    </>
  );
  return isMobile ? (
    <SwipeableDrawer
      style={zIndex ? { zIndex } : undefined}
      anchor="bottom"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableDiscovery
      disableSwipeToOpen
      PaperProps={{
        className: classes.paper,
        style: { maxHeight: `${maxHeightPercent}%` },
        role: "dialog",
        "aria-label": title,
      }}
      ModalProps={{ BackdropProps: { className: classes.backdrop } }}
    >
      {content}
    </SwipeableDrawer>
  ) : (
    <Dialog
      style={zIndex ? { zIndex } : undefined}
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: `${classes.paper} ${classes.pcPaper}`,
        style: { maxHeight: `${maxHeightPercent}%` },
        "aria-label": title,
      }}
      BackdropProps={{ className: classes.backdrop }}
    >
      {content}
    </Dialog>
  );
};
